import { baseNameOf, languageOf } from '../contract/defaults';
import { REGIONAL_DEFAULTS } from '../contract/regional';
import { numberFormat } from '../format/intl-cache';
import {
  FALLBACK_LABEL_SEPARATOR,
  GAP_LABEL_CATALOG,
  GAP_LABEL_OVERRIDES,
  type GapUnitCode,
  INTL_UNIT_BY_CODE,
  REFERENCE_INTL_UNIT,
} from './catalog';
import type { QuantityKind, UnitFor } from './registry';

/**
 * What to call a unit, in the user's language.
 *
 * The two sources are asymmetric on purpose. For a unit CLDR covers, the label is read back out of
 * the same formatter that will render the value, so a label shown in a column header and the
 * suffix shown in its cells cannot disagree — even after an ICU upgrade changes one of them. For a
 * unit CLDR does not cover, the label comes from the package's own dictionary.
 */

/**
 * How many labels are held at once.
 *
 * A label is constant for a locale, kind and unit, but `formatMeasurement` asks for one on every
 * value it renders, so a table of a thousand cells would otherwise repeat the same ICU work a
 * thousand times. One reader exercises at most the twenty-two unit codes plus a separator, so the
 * bound is far above a real session and a caller sweeping locales degrades to the uncached cost
 * rather than growing the heap for the life of the tab.
 */
const LABEL_CACHE_LIMIT = 200;

const labels = new Map<string, string>();

const memoised = (key: string, build: () => string): string => {
  const hit = labels.get(key);
  if (hit !== undefined) return hit;

  const built = build();

  // Cleared wholesale rather than evicted one at a time: past the bound the working set has already
  // been left behind, and a label costs little enough to rebuild that tracking recency would not pay
  // for itself.
  if (labels.size >= LABEL_CACHE_LIMIT) labels.clear();

  labels.set(key, built);
  return built;
};

/** Empties the label cache. For tests that change what ICU answers between cases. */
export const resetLabelCache = (): void => {
  labels.clear();
};

/**
 * The `Intl` identifier for a unit, or `null` when CLDR has no name for it.
 *
 * Exported because the formatter branches on the same answer this module does, and one lookup
 * shared between them is one fewer place for the two to disagree.
 */
export const intlIdentifierFor = <K extends QuantityKind>(
  kind: K,
  unit: UnitFor<K>
): string | null => {
  // The table is total per kind, but indexing a union of per-kind records loses that: TypeScript
  // cannot see that `kind` and `unit` are correlated. The lookup is exhaustive by construction.
  const identifiers = INTL_UNIT_BY_CODE[kind] as Readonly<
    Record<string, string | null>
  >;

  return identifiers[unit] ?? null;
};

const ownLabelFor = (locale: string, unit: GapUnitCode): string => {
  // Keyed by market, so a tag carrying a Unicode extension has to be reduced before the lookup:
  // `fr-CA-u-nu-latn` would otherwise miss `fr-CA` and read the generic `psi`.
  const override = GAP_LABEL_OVERRIDES[baseNameOf(locale) ?? locale]?.[unit];
  if (override) return override;

  const language = languageOf(locale) ?? REGIONAL_DEFAULTS.language;
  const labels =
    GAP_LABEL_CATALOG[language] ??
    GAP_LABEL_CATALOG[REGIONAL_DEFAULTS.language];

  // The fallback row is a compile-time-total record, so this only narrows a type the seeding
  // already guarantees.
  return labels?.[unit] ?? unit;
};

/**
 * What this locale puts between a number and its unit.
 *
 * Asked of ICU rather than assumed, because locales disagree and the disagreement is invisible:
 * fr-FR separates the two with a narrow no-break space and fr-CA with a no-break space, where the
 * other locales this product serves use a plain one. Hardcoding any of them would make a French
 * table show one spacing for kilometres, which CLDR renders, and another for kilowatt-hours, which
 * the dictionary does.
 *
 * @param locale - The user's resolved locale.
 * @returns The separator, or a plain space if ICU renders the reference unit without one.
 */
export const labelSeparatorFor = (locale: string): string =>
  // A null character cannot appear in a language tag, so no locale can collide with the separator.
  memoised(`separator\u0000${locale}`, () => {
    const literal = numberFormat(locale, {
      style: 'unit',
      unit: REFERENCE_INTL_UNIT,
      unitDisplay: 'short',
    })
      .formatToParts(1)
      .find(({ type }) => type === 'literal');

    return literal?.value ?? FALLBACK_LABEL_SEPARATOR;
  });

/**
 * The short label for a unit.
 *
 * @param locale - The user's resolved locale.
 * @param kind - The quantity the unit measures.
 * @param unit - The unit code.
 * @returns The abbreviation, such as `km`, `mpg` or `kWh`. Never empty.
 */
export const labelFor = <K extends QuantityKind>(
  locale: string,
  kind: K,
  unit: UnitFor<K>
): string =>
  memoised(`label\u0000${locale}\u0000${kind}\u0000${unit}`, () => {
    const identifier = intlIdentifierFor(kind, unit);

    if (identifier === null) return ownLabelFor(locale, unit as GapUnitCode);

    // One formatted sample, reduced to its unit parts. A compound label such as `km/h` arrives as a
    // single part, so this is a join of one in every case ICU currently produces; it is written as a
    // join anyway because nothing in the specification promises that.
    return numberFormat(locale, {
      style: 'unit',
      unit: identifier,
      unitDisplay: 'short',
    })
      .formatToParts(1)
      .filter(({ type }) => type === 'unit')
      .map(({ value }) => value)
      .join('');
  });
